import React, { useEffect, useMemo, useState } from 'react';
import { logger } from '../../utils/logger';
import { ClipboardListIcon, SearchIcon, UserIcon, ClockIcon, ShieldAlertIcon, DownloadIcon, ArchiveIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, LockIcon, PencilIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { exportAuditLogsToExcel } from '../../utils/excelExport';
import { exportAuditLogsToPdf } from '../../utils/pdfExport';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
// ============ SAFE HELPERS ============
function toStr(v: any): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) {
    try {
      return v.toISOString();
    } catch {
      return '';
    }
  }
  try {
    return JSON.stringify(v);
  } catch {
    return '';
  }
}
function fmtDate(date: any): string {
  try {
    if (!date) return 'Sin fecha';
    const d = date instanceof Date ? date : new Date(typeof date === 'string' || typeof date === 'number' ? date : Date.now());
    if (isNaN(d.getTime())) return 'Fecha inválida';
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) + ' • ' + d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'Sin fecha';
  }
}
const MODULE_NAMES: Record<string, string> = {
  patients: 'Pacientes',
  doctors: 'Doctores',
  appointments: 'Citas',
  services: 'Servicios',
  insurance: 'Seguros',
  insurances: 'Seguros',
  specialties: 'Especialidades',
  contacts: 'Contactos',
  companies: 'Empresas',
  opportunities: 'Oportunidades',
  clinicalHistory: 'Historial Clínico',
  clinical_histories: 'Historial Clínico',
  followUpNotes: 'Notas de Seguimiento',
  follow_up_notes: 'Notas de Seguimiento',
  auditLog: 'Bitácora',
  audit_logs: 'Bitácora',
  settings: 'Configuración',
  dashboard: 'Panel de Control',
  roles: 'Roles',
  permissions: 'Permisos',
  products: 'Productos',
  product_sales: 'Ventas de Productos',
  sessionPackages: 'Paquetes de Sesiones',
  giftCertificates: 'Certificados de Regalo',
  offers: 'Ofertas'
};
function moduleName(m: string): string {
  return MODULE_NAMES[m] || (m ? m.charAt(0).toUpperCase() + m.slice(1) : 'Módulo');
}
// ============ TYPES ============
interface LogItem {
  id: string;
  ts: Date;
  userId: string;
  userName: string;
  action: string;
  module: string;
  entityId: string;
  entityName: string;
  details: string;
  changes?: {
    field: string;
    oldValue: string;
    newValue: string;
  }[];
}
function parseLog(raw: any): LogItem | null {
  try {
    if (!raw || typeof raw !== 'object') return null;
    return {
      id: toStr(raw.id) || String(Math.random()),
      ts: raw.timestamp instanceof Date ? raw.timestamp : new Date(typeof raw.timestamp === 'string' || typeof raw.timestamp === 'number' ? raw.timestamp : Date.now()),
      userId: toStr(raw.userId),
      userName: toStr(raw.userName),
      action: toStr(raw.action),
      module: toStr(raw.module),
      entityId: toStr(raw.entityId),
      entityName: toStr(raw.entityName),
      details: toStr(raw.details),
      changes: Array.isArray(raw.changes) ? raw.changes.map((c: any) => ({
        field: toStr(c.field),
        oldValue: toStr(c.oldValue),
        newValue: toStr(c.newValue)
      })) : undefined
    };
  } catch {
    return null;
  }
}
// ============ SUB-COMPONENTS (inline to avoid import issues) ============
function ActionBadge({
  action
}: {
  action: string;
}) {
  switch (action) {
    case 'create':
      return <Badge variant="success" data-id="element-776" data-mp="es5zt6dxturt">{'Creación'}</Badge>;
    case 'update':
      return <Badge variant="info" data-id="element-777" data-mp="eu90qlgmp8bb">{'Edición'}</Badge>;
    case 'delete':
      return <Badge variant="danger" data-id="element-778" data-mp="jpxug85yvuzp">{'Eliminación'}</Badge>;
    case 'complete':
      return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" data-id="element-779" data-mp="oreuy2kjhffr">
          {'Completado'}
        </Badge>;
    case 'cancel':
      return <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400" data-id="element-780" data-mp="2e7r1gsahtqyq">
          {'Cancelado'}
        </Badge>;
    case 'reschedule':
      return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" data-id="element-781" data-mp="2491nvbpmqsoa">
          {'Reprogramado'}
        </Badge>;
    default:
      return <Badge variant="default" data-id="element-782" data-mp="1zcb34b2vr8wj">{action || 'Acción'}</Badge>;
  }
}
function LogRow({
  log,
  viewMode,
  onArchive,
  onDeactivate,
  onRestore,
  onClick
}: {
  log: LogItem;
  viewMode: string;
  onArchive: (id: string) => void;
  onDeactivate: (id: string) => void;
  onRestore: (id: string) => void;
  onClick: () => void;
}) {
  return <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group cursor-pointer" onClick={onClick} data-id="element-783" data-mp="2i9m6a6rmumh9">
      <td className="py-4 px-6 whitespace-nowrap" data-id="element-784" data-mp="3fq19johh299c">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-785" data-mp="3ay00ebfloynj">
          <ClockIcon className="w-4 h-4 text-gray-400" data-id="element-786" data-mp="190g44fa28fvc" />
          <span data-id="element-787" data-mp="190ptc0na6izz">{fmtDate(log.ts)}</span>
        </div>
      </td>
      <td className="py-4 px-6 whitespace-nowrap" data-id="element-788" data-mp="1dzkc52l907k3">
        <div className="flex items-center gap-2" data-id="element-789" data-mp="1e1dishvv2rs3">
          <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center text-teal-600 dark:text-teal-400" data-id="element-790" data-mp="zb69arnu0j3j">
            <UserIcon className="w-4 h-4" data-id="element-791" data-mp="147ekjtywrs5f" />
          </div>
          <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-792" data-mp="q8n9kgfcemli">
            {log.userName}
          </span>
        </div>
      </td>
      <td className="py-4 px-6 whitespace-nowrap" data-id="element-793" data-mp="u8yu7t7pgmwz">
        <ActionBadge action={log.action} data-id="element-794" data-mp="gck8zf052w0o" />
      </td>
      <td className="py-4 px-6 whitespace-nowrap hidden sm:table-cell" data-id="element-795" data-mp="gdvat9a0xtp5">
        <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-796" data-mp="lavl5wdwpim2">
          {moduleName(log.module)}
        </span>
      </td>
      <td className="py-4 px-6 hidden sm:table-cell" data-id="element-797" data-mp="2fomq4ilajeb6">
        <p className="text-sm text-gray-900 dark:text-gray-200" data-id="element-798" data-mp="2fnbpusr20oot">
          {log.details}
        </p>
        {log.entityName !== '' && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-799" data-mp="3yyd12uv15vn">
            {'Entidad: '}
            {log.entityName}
          </p>}
      </td>
      <td className="py-4 px-6" data-id="element-800" data-mp="dsnmg6ii84e7">
        <div className="flex items-center justify-end gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-801" data-mp="dsxfy42rcton">
          {viewMode === 'active' ? <>
              <button onClick={e => {
            e.stopPropagation();
            onArchive(log.id);
          }} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-802" data-mp="mtvjkl9aaz6p">
                <ArchiveIcon className="w-4 h-4" data-id="element-803" data-mp="2ygtjgohwl730" />
              </button>
              <button onClick={e => {
            e.stopPropagation();
            onDeactivate(log.id);
          }} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-804" data-mp="2ti8i2lqx0hcz">
                <PowerOffIcon className="w-4 h-4" data-id="element-805" data-mp="2hfxhl8y53cvt" />
              </button>
            </> : <button onClick={e => {
          e.stopPropagation();
          onRestore(log.id);
        }} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-806" data-mp="2mcxofm2tcjnz">
              <RotateCcwIcon className="w-4 h-4" data-id="element-807" data-mp="1yn3b73el0t37" />
            </button>}
        </div>
      </td>
    </tr>;
}
// ============ ENTITY CONTEXT (shows related entity details in modal) ============
function EntityContext({
  log
}: {
  log: LogItem;
}) {
  const {
    appointments,
    patients,
    doctors,
    services,
    getPatient,
    getDoctor,
    getService
  } = useData();
  if (!log.entityId) return null;
  const mod = log.module;
  // Appointments
  if (mod === 'appointments') {
    const apt = appointments.find(a => a.id === log.entityId);
    if (!apt) return null;
    const patient = getPatient(apt.patientId);
    const doctor = getDoctor(apt.doctorId);
    const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
    const serviceNames = serviceIds.map(sId => getService(sId)?.name).filter(Boolean);
    return <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl p-4 border border-teal-100 dark:border-teal-800/30" data-id="element-808" data-mp="aqm3bqnidwp3">
        <p className="text-xs font-medium text-teal-600 dark:text-teal-400 uppercase tracking-wider mb-3" data-id="element-809" data-mp="5s2dvg9pi2rz">
          Información de la Cita
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-810" data-mp="253vctig21i7c">
          <div data-id="element-811" data-mp="206lgdiu906a0">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-812" data-mp="2063bxyxfmlr0">Paciente</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-813" data-mp="rq2yzuo5f4w8">
              {patient?.name || apt.patientId}
            </p>
          </div>
          <div data-id="element-814" data-mp="10ru3wxav77p1">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-815" data-mp="10rkfhe5gx8lg">Doctor</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-816" data-mp="10pgaesvu23jl">
              {doctor?.name || 'Sin asignar'}
            </p>
          </div>
          <div data-id="element-817" data-mp="83ybw5w8q3yl">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-818" data-mp="83es0jn155hx">
              Fecha de la Cita
            </p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-819" data-mp="3zqihveu7zu1">
              {apt.date}
            </p>
          </div>
          <div data-id="element-820" data-mp="d1q0apxof7y1">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-821" data-mp="d16sw35nk1y9">Hora</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-822" data-mp="3lhjgt4s0cjf3">
              {apt.time || 'N/A'}
            </p>
          </div>
          {serviceNames.length > 0 && <div className="sm:col-span-2" data-id="element-823" data-mp="1w1630zztc09j">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-824" data-mp="1w04h20h9dtd1">
                Servicio(s)
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-825" data-mp="20sxl0m4wkxo2">
                {serviceNames.join(', ')}
              </p>
            </div>}
          <div data-id="element-826" data-mp="1s0dr1yzk3h9k">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-827" data-mp="1rzu8qh5zuqv0">Estado</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize" data-id="element-828" data-mp="2fpyccoj6mqm0">
              {apt.status}
            </p>
          </div>
          {apt.notes && <div className="sm:col-span-2" data-id="element-829" data-mp="25r8tasfjv046">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-830" data-mp="25q7f4f38lxi8">Notas</p>
              <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-831" data-mp="2aor7ax8fc5j1">
                {apt.notes}
              </p>
            </div>}
        </div>
      </div>;
  }
  // Patients
  if (mod === 'patients') {
    const patient = patients.find(p => p.id === log.entityId);
    if (!patient) return null;
    return <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-emerald-100 dark:border-emerald-800/30" data-id="element-832" data-mp="f7r4ep5rrgy1">
        <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-3" data-id="element-833" data-mp="6g8q6jsdvb7l">
          Información del Paciente
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-834" data-mp="1gmbs70tjox7">
          <div data-id="element-835" data-mp="3snp4j2wb239j">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-836" data-mp="3so8bxpobx99b">Nombre</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-837" data-mp="3no2rouke2p19">
              {patient.name}
            </p>
          </div>
          <div data-id="element-838" data-mp="3jn0qi86ww2wn">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-839" data-mp="3jov2uy44amp4">Teléfono</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-840" data-mp="3epieg8xll7kf">
              {patient.phone || 'N/A'}
            </p>
          </div>
          <div data-id="element-841" data-mp="1cvridm42m7m3">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-842" data-mp="1cw16131n82om">Email</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-843" data-mp="1kg3erc6s3ols">
              {patient.email || 'N/A'}
            </p>
          </div>
          <div data-id="element-844" data-mp="1pdbyhh7ks0p5">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-845" data-mp="1pctvm1qeqo89">Género</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize" data-id="element-846" data-mp="1u6556amltj3v">
              {patient.gender || 'N/A'}
            </p>
          </div>
        </div>
      </div>;
  }
  // Doctors
  if (mod === 'doctors') {
    const doctor = doctors.find(d => d.id === log.entityId);
    if (!doctor) return null;
    return <div className="bg-violet-50 dark:bg-violet-900/20 rounded-xl p-4 border border-violet-100 dark:border-violet-800/30" data-id="element-847" data-mp="1bf5bwx82tn4s">
        <p className="text-xs font-medium text-violet-600 dark:text-violet-400 uppercase tracking-wider mb-3" data-id="element-848" data-mp="1fgz92vj2c907">
          Información del Doctor
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-849" data-mp="2mwn738bldefp">
          <div data-id="element-850" data-mp="2qukuc5lsxqgg">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-851" data-mp="2qu2t0uka8m1o">Nombre</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-852" data-mp="2pfi6hdyxrt43">
              {doctor.name}
            </p>
          </div>
          <div data-id="element-853" data-mp="2ki06id2wco88">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-854" data-mp="2zcmw5nrzq6pn">Teléfono</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-855" data-mp="2zajwec11cz7q">
              {doctor.phone || 'N/A'}
            </p>
          </div>
          <div data-id="element-856" data-mp="38cjel7nse9c9">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-857" data-mp="38c076kvrj3ch">Email</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-858" data-mp="33f9p5s0edxof">
              {doctor.email || 'N/A'}
            </p>
          </div>
        </div>
      </div>;
  }
  // Services
  if (mod === 'services') {
    const service = services.find(s => s.id === log.entityId);
    if (!service) return null;
    return <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-100 dark:border-amber-800/30" data-id="element-859" data-mp="3r4c7l22uvujz">
        <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-3" data-id="element-860" data-mp="3n0o0eki4z0z9">
          Información del Servicio
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-861" data-mp="ej6tns9kbg2j">
          <div data-id="element-862" data-mp="kyynpw36ou0">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-863" data-mp="li62co41uts">Nombre</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-864" data-mp="5jjrv5cx888y">
              {service.name}
            </p>
          </div>
          <div data-id="element-865" data-mp="3mzi42x332toc">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-866" data-mp="3myyv45vevfmg">Duración</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-867" data-mp="3rrrzgsmywly0">
              {service.duration} min
            </p>
          </div>
          <div data-id="element-868" data-mp="3i16tgk4ysf52">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-869" data-mp="3i0ozxv9osf0u">Precio</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-870" data-mp="341fk4wpe7mxe">
              RD$ {service.price.toLocaleString()}
            </p>
          </div>
        </div>
      </div>;
  }
  return null;
}
// ============ MAIN COMPONENT ============
export function AuditLog() {
  const dataCtx = useData();
  const {
    user
  } = useAuth();
  const permCtx = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [itemStatuses, setItemStatuses] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState('active');
  const [confirmAction, setConfirmAction] = useState<{
    id: string;
    type: string;
  } | null>(null);
  const [selectedLog, setSelectedLog] = useState<LogItem | null>(null);
  const readOnly = permCtx.isReadOnly('auditLog');
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  // Load statuses from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-audit-log-status');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setItemStatuses(parsed);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);
  // Save statuses to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('savidna-audit-log-status', JSON.stringify(itemStatuses));
    } catch {
      /* ignore */
    }
  }, [itemStatuses]);
  // Parse all logs once
  const allLogs = useMemo<LogItem[]>(() => {
    const result: LogItem[] = [];
    const raw = dataCtx.auditLogs;
    if (!Array.isArray(raw)) return result;
    for (let i = 0; i < raw.length; i++) {
      const parsed = parseLog(raw[i]);
      if (parsed) result.push(parsed);
    }
    return result;
  }, [dataCtx.auditLogs]);
  const getStatus = (id: string): string => itemStatuses[id] || 'active';
  // Filter logs
  const filteredLogs = useMemo<LogItem[]>(() => {
    const search = (searchTerm || '').toLowerCase();
    return allLogs.filter(log => {
      if (getStatus(log.id) !== viewMode) return false;
      if (filterModule && log.module !== filterModule) return false;
      if (filterAction && log.action !== filterAction) return false;
      if (search) {
        const inDetails = log.details.toLowerCase().includes(search);
        const inUser = log.userName.toLowerCase().includes(search);
        const inEntity = log.entityName.toLowerCase().includes(search);
        if (!inDetails && !inUser && !inEntity) return false;
      }
      return true;
    });
  }, [allLogs, searchTerm, filterModule, filterAction, viewMode, itemStatuses]);
  const {
    sortedItems: sortedLogs,
    requestSort: logRequestSort,
    isSorted: logIsSorted
  } = useSortableData(filteredLogs, {
    key: 'ts',
    direction: 'desc'
  });
  const pagination = usePagination(sortedLogs, {
    itemsPerPage: 10
  });
  // Counts
  const activeCount = useMemo(() => allLogs.filter(l => getStatus(l.id) === 'active').length, [allLogs, itemStatuses]);
  const archivedCount = useMemo(() => allLogs.filter(l => getStatus(l.id) === 'archived').length, [allLogs, itemStatuses]);
  const deactivatedCount = useMemo(() => allLogs.filter(l => getStatus(l.id) === 'deactivated').length, [allLogs, itemStatuses]);
  // Module options for filter
  const moduleOptions = useMemo(() => {
    const set = new Set<string>();
    allLogs.forEach(l => {
      if (l.module) set.add(l.module);
    });
    return Array.from(set).map(m => ({
      value: m,
      label: moduleName(m)
    }));
  }, [allLogs]);
  const actionOptions: {
    value: string;
    label: string;
  }[] = [{
    value: 'create',
    label: 'Creación'
  }, {
    value: 'update',
    label: 'Actualización'
  }, {
    value: 'delete',
    label: 'Eliminación'
  }, {
    value: 'complete',
    label: 'Completado'
  }, {
    value: 'cancel',
    label: 'Cancelación'
  }, {
    value: 'reschedule',
    label: 'Reprogramación'
  }];
  // Actions
  const handleArchive = (id: string) => {
    setItemStatuses(p => ({
      ...p,
      [id]: 'archived'
    }));
    setConfirmAction(null);
  };
  const handleDeactivate = (id: string) => {
    setItemStatuses(p => ({
      ...p,
      [id]: 'deactivated'
    }));
    setConfirmAction(null);
  };
  const handleRestore = (id: string) => {
    setItemStatuses(p => {
      const n = {
        ...p
      };
      delete n[id];
      return n;
    });
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    try {
      await new Promise(r => setTimeout(r, 200));
      let dataToExport = dataCtx.auditLogs as any;
      if (dateFrom && dateTo) {
        dataToExport = (dataCtx.auditLogs as any[]).filter((log: any) => {
          const dateStr = new Date(log.timestamp).toISOString().split('T')[0];
          return dateStr >= dateFrom && dateStr <= dateTo;
        });
      }
      exportAuditLogsToExcel(dataToExport);
    } catch (e) {
      logger.error(e);
    }
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = dataCtx.auditLogs as any[];
    if (dateFrom && dateTo) {
      dataToExport = (dataCtx.auditLogs as any[]).filter((log: any) => {
        const dateStr = new Date(log.timestamp).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportAuditLogsToPdf(dataToExport);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  // Permission check
  if (!permCtx.canView('auditLog')) {
    return <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6" data-id="element-871" data-mp="b3hrq9o56cjw">
        <ShieldAlertIcon className="w-16 h-16 text-gray-300 mb-4" data-id="element-872" data-mp="fu7z1sg4oph8" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-873" data-mp="fuzw77pdiv9m">
          {'Acceso Restringido'}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md" data-id="element-874" data-mp="pse0k8y0vtlk">
          {'No tienes permisos para ver la bitácora de auditoría.'}
        </p>
      </div>;
  }
  const items = pagination.paginatedItems;
  return <div className="space-y-6" data-id="element-875" data-mp="2f99hlubarr8j">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-purple-500 to-indigo-500 shadow-xl text-white" data-id="element-876" data-mp="2faugtx8k3ycq">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,1) 25%, transparent 25%), linear-gradient(225deg, rgba(255,255,255,1) 25%, transparent 25%), linear-gradient(315deg, rgba(255,255,255,1) 25%, transparent 25%), linear-gradient(45deg, rgba(255,255,255,1) 25%, transparent 25%)',
        backgroundSize: '20px 20px',
        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
      }} data-id="element-877" data-mp="25c4w7wp9zzss" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-878" data-mp="3gqs6yg9s26aa" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-879" data-mp="3bxpe6actr0v5" />

        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-880" data-mp="2wjvd2qm7i5pd">
          <div data-id="element-881" data-mp="2mjme550ty66a">
            <div className="flex items-center gap-3 mb-2" data-id="element-882" data-mp="2mk4fgg2cnal2">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-883" data-mp="2mlpdwgrsbdo7">
                <ClipboardListIcon className="w-6 h-6" data-id="element-884" data-mp="2rj7lo3u2fmum" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-885" data-mp="3f9bdldxw5y5s">{'Bitácora de Auditoría'}</h1>
            </div>
            <p className="text-white/80 text-sm font-medium" data-id="element-886" data-mp="3k7mrq0v7yutt">
              {'Registro de actividades y cambios en el sistema'}
              {readOnly ? <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-887" data-mp="35gmdtgbpe8qa">
                  {'Solo lectura'}
                </span> : null}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-888" data-mp="6sgkurat5u5f">
            {canExportPdf && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-890" data-mp="1rhknf0i7bcim" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-889" data-mp="6ujmk8lbpbpz">
                Exportar PDF
              </Button>}
            {canExportExcel && filteredLogs.length > 0 ? <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-892" data-mp="18m4044pv50v4" />} onClick={() => setIsExportModalOpen(true)} isLoading={isExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-891" data-mp="18jrkvzbhp4lr">
                Exportar Excel
              </Button> : null}
          </div>
        </div>
      </div>

      {readOnly ? <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-893" data-mp="2t1xuv7c830am">
          <LockIcon className="w-4 h-4" data-id="element-894" data-mp="1ln3zwzthb1jh" />
          <span data-id="element-895" data-mp="1qm6v7sjnpte9">
            {'Solo tienes permisos de lectura. No puedes modificar los registros de auditoría.'}
          </span>
        </div> : null}

      {/* Filters */}
      <Card data-id="element-896" data-mp="1znmzrjnsj1ak">
        <div className="flex flex-col lg:flex-row gap-4" data-id="element-897" data-mp="1zm3ns5963cel">
          <div className="flex-1 relative" data-id="element-898" data-mp="12wumsuw5lpd0">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-899" data-mp="12y5omp61gn1h" />
            <Input style={{
            paddingLeft: '2.5rem'
          }} placeholder="Buscar por usuario, entidad o detalles..." value={searchTerm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)} className="" data-id="element-900" data-mp="17w75d3fzsa9s" />
          </div>
          <div className="w-full lg:w-48" data-id="element-901" data-mp="o6ey364j3za3">
            <Select options={[{
            value: '',
            label: 'Todos los módulos'
          }, ...moduleOptions]} value={filterModule} onChange={setFilterModule} placeholder="Módulo" data-id="element-902" data-mp="gogpr4y32u8g" />
          </div>
          <div className="w-full lg:w-48" data-id="element-903" data-mp="2nw9ogjjzcin">
            <Select options={[{
            value: '',
            label: 'Todas las acciones'
          }, ...actionOptions]} value={filterAction} onChange={setFilterAction} placeholder="Acción" data-id="element-904" data-mp="3tuyzp4nnncra" />
          </div>
        </div>
      </Card>

      {/* View Mode Tabs */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-905" data-mp="3b4hk81ru2dnv">
        <button onClick={() => setViewMode('active')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === 'active' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-906" data-mp="3654n8o78zq7k">
          <CheckCircleIcon className="w-4 h-4" data-id="element-907" data-mp="tv3a5y6hf23b" />
          {'Activos'}
          <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-908" data-mp="tx7l302st1d3">
            {String(activeCount)}
          </span>
        </button>
        <button onClick={() => setViewMode('archived')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === 'archived' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-909" data-mp="jxyu8g38c2uy">
          <ArchiveIcon className="w-4 h-4" data-id="element-910" data-mp="16yzey91zytr" />
          {'Archivados'}
          <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-911" data-mp="18a36y2i16kv">
            {String(archivedCount)}
          </span>
        </button>
        <button onClick={() => setViewMode('deactivated')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === 'deactivated' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-912" data-mp="38s8jh1nd629d">
          <XCircleIcon className="w-4 h-4" data-id="element-913" data-mp="3rqkrul6gb83w" />
          {'Desactivados'}
          <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-914" data-mp="a91oldt6nlve">
            {String(deactivatedCount)}
          </span>
        </button>
      </div>

      {/* Table */}
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-915" data-mp="j9q2m8iktm8a">
        <div className="h-1 bg-gradient-to-r from-purple-500 to-indigo-500" data-id="element-916" data-mp="j7wvyt7yr20a" />
        {/* ── MOBILE CARDS ── */}
        <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-917" data-mp="1gn2xgzgxgq2a">
          {items.length === 0 ? <div className="py-10 text-center text-gray-500 dark:text-gray-400" data-id="element-918" data-mp="1bnhywq5o6jq3">
              <p className="text-sm" data-id="element-919" data-mp="16owvkhv4fjxf">No se encontraron registros</p>
            </div> : items.map((log, idx) => <div key={log.id + '-m-' + String(idx)} onClick={() => setSelectedLog(log)} className="px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-920" data-mp="12ma2yeqmluz6" data-mpi={idx}>
                <div className="flex items-start justify-between gap-2" data-id="element-921" data-mp="nyf74phbnahv">
                  <div className="flex-1 min-w-0" data-id="element-922" data-mp="nyoxie6a3jo3">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-923" data-mp="izuf3dc83x4i">
                      <ActionBadge action={log.action} data-id="element-924" data-mp="exr40ptguujr" />
                      <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-925" data-mp="ex7lp7zwm457">
                        {fmtDate(log.ts)}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 truncate" data-id="element-926" data-mp="2lmsvetecmjp0">
                      {log.userName}
                    </p>
                    {log.entityName && <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-927" data-mp="2unr0lf0iwx96">
                        {log.entityName}
                      </p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-928" data-mp="22wjesehtepds">
                    {viewMode === 'active' && <button onClick={e => {
                e.stopPropagation();
                setConfirmAction({
                  id: log.id,
                  type: 'archive'
                });
              }} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" data-id="element-929" data-mp="26xd49cawoc8s">
                        <ArchiveIcon className="w-4 h-4" data-id="element-930" data-mp="2rgzrr36aihgv" />
                      </button>}
                    {viewMode !== 'active' && <button onClick={e => {
                e.stopPropagation();
                handleRestore(log.id);
              }} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" data-id="element-931" data-mp="2mgc4msl6mkrx">
                        <RotateCcwIcon className="w-4 h-4" data-id="element-932" data-mp="23k3t571ktdo6" />
                      </button>}
                  </div>
                </div>
              </div>)}
        </div>

        {/* ── DESKTOP TABLE ── */}
        <div className="hidden sm:block overflow-x-auto" data-id="element-933" data-mp="2cixoku367y05">
          <table className="w-full" data-id="element-934" data-mp="3sxpff2ko4x7c">
            <thead data-id="element-935" data-mp="3swntu4610sbd">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-936" data-mp="3t0bpp9yy1qt9">
                <SortableHeader label="Fecha y Hora" sortKey="ts" currentDirection={logIsSorted('ts')} onSort={logRequestSort} data-id="element-937" data-mp="b1wq1sz3d2t" />

                <SortableHeader label="Usuario" sortKey="userName" currentDirection={logIsSorted('userName')} onSort={logRequestSort} data-id="element-938" data-mp="3miryq1ugw07a" />

                <SortableHeader label="Acción" sortKey="action" currentDirection={logIsSorted('action')} onSort={logRequestSort} data-id="element-939" data-mp="j2uo5rse8q8f" />

                <SortableHeader label="Módulo" sortKey="module" currentDirection={logIsSorted('module')} onSort={logRequestSort} className="hidden sm:table-cell" data-id="element-940" data-mp="9bghn020yq5s" />

                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell" data-id="element-941" data-mp="16s5s1i51j0qs">
                  {'Detalles'}
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-942" data-mp="3a8aqdeez1epn">
                  {'Acciones'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-943" data-mp="30cquut4bki1f">
              {items.length === 0 ? <tr data-id="element-944" data-mp="1v3eorg5c4n4">
                  <td colSpan={6} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-945" data-mp="3sztu5ngtkwsq">
                    <div className="flex flex-col items-center justify-center" data-id="element-946" data-mp="3o70pt0p9jqh6">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-947" data-mp="3j8z222gw72zd">
                        <ClipboardListIcon className="w-8 h-8 text-gray-400" data-id="element-948" data-mp="kll68sp8su6w" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-949" data-mp="fr8ja8ok50qe">
                        {'No se encontraron registros'}
                      </h3>
                      <p className="text-sm" data-id="element-950" data-mp="s3jbgl0e5j4b">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'La bitácora está vacía' : 'No hay registros en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : items.map((log, idx) => <LogRow key={log.id + '-' + String(idx)} log={log} viewMode={viewMode} onClick={() => setSelectedLog(log)} onArchive={id => setConfirmAction({
              id,
              type: 'archive'
            })} onDeactivate={id => setConfirmAction({
              id,
              type: 'deactivate'
            })} onRestore={handleRestore} data-id="element-951" data-mp="1fu6mvg9imqvm" data-mpi={idx} />)}
            </tbody>
          </table>
        </div>
        {/* Shared pagination */}
        {pagination.totalPages > 1 && <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-952" data-mp="dgh781uvvvcn">
            <Pagination currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalItems={pagination.totalItems} startIndex={pagination.startIndex} endIndex={pagination.endIndex} onPageChange={pagination.goToPage} onNext={pagination.nextPage} onPrev={pagination.prevPage} data-id="element-953" data-mp="8f1l06gyziso" />
          </div>}
      </Card>

      {/* Confirm Modal */}
      {confirmAction !== null ? <Modal isOpen={true} onClose={() => setConfirmAction(null)} title={confirmAction.type === 'archive' ? 'Archivar Registro' : 'Desactivar Registro'} size="sm" data-id="element-954" data-mp="2a306dy3wlm9h">
          <div className="space-y-4" data-id="element-955" data-mp="258vxvaezp66a">
            <p className="text-gray-600 dark:text-gray-400" data-id="element-956" data-mp="257ufslzk3jf2">
              {confirmAction.type === 'archive' ? '¿Estás seguro de que deseas archivar este registro? Podrás restaurarlo más tarde.' : '¿Estás seguro de que deseas desactivar este registro? No aparecerá en las listas activas.'}
            </p>
            <div className="flex justify-end gap-3 modal-actions" data-id="element-957" data-mp="1majgx2hu6s05">
              <Button variant="outline" onClick={() => setConfirmAction(null)} data-id="element-958" data-mp="3bq9c5udoq0qg">
                {'Cancelar'}
              </Button>
              <Button variant="primary" onClick={() => {
            if (confirmAction.type === 'archive') handleArchive(confirmAction.id);else handleDeactivate(confirmAction.id);
          }} data-id="element-959" data-mp="3gplxgaowqzqx">
                {confirmAction.type === 'archive' ? 'Archivar' : 'Desactivar'}
              </Button>
            </div>
          </div>
        </Modal> : null}

      {/* Detail Modal */}
      {selectedLog !== null ? <Modal isOpen={true} onClose={() => setSelectedLog(null)} title="Detalle del Registro" size="md" icon={<ClipboardListIcon className="w-5 h-5" data-id="element-961" data-mp="fpzladhl4yb" />} accentColor="purple" data-id="element-960" data-mp="aep0g6s0fwar">
          <div className="space-y-5" data-id="element-962" data-mp="3sjijug3ysnba">
            {/* Action & Status */}
            <div className="flex items-center gap-3" data-id="element-963" data-mp="3slbus7lm1tp3">
              <ActionBadge action={selectedLog.action} data-id="element-964" data-mp="1uu0thx8xupay" />
              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-965" data-mp="1urelxxm1s9sq">
                en {moduleName(selectedLog.module)}
              </span>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-966" data-mp="38no7lnttymik">
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700" data-id="element-967" data-mp="3dnkcdpacqhz6">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-968" data-mp="3ihomuiitt850">
                  Fecha y Hora
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-969" data-mp="2pry4fxa5vk33">
                  <ClockIcon className="w-4 h-4 text-purple-500" data-id="element-970" data-mp="2uqsr5ab91ssh" />
                  {fmtDate(selectedLog.ts)}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700" data-id="element-971" data-mp="33qfqlqkf4oiw">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-972" data-mp="e259z9oteazn">
                  Usuario
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-973" data-mp="j29urbwr9rvo">
                  <UserIcon className="w-4 h-4 text-teal-500" data-id="element-974" data-mp="lc6z9s7l5e25" />
                  {selectedLog.userName || 'Sin usuario'}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700" data-id="element-975" data-mp="pc8r79niqt48">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-976" data-mp="uaiktftlhlnq">
                  Módulo
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-977" data-mp="149rmkuupc4kn">
                  {moduleName(selectedLog.module)}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700" data-id="element-978" data-mp="1d3wb37md53x0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-979" data-mp="1i3sbyxzrkf8c">
                  ID de Entidad
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white font-mono text-xs break-all" data-id="element-980" data-mp="1x60i6gy8pae8">
                  {selectedLog.entityId || 'N/A'}
                </p>
              </div>
            </div>

            {/* Entity Name */}
            {selectedLog.entityName && <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700" data-id="element-981" data-mp="1j5ziv5xmib0v">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-982" data-mp="2fuxdmo5di8y1">
                  Entidad
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-983" data-mp="2665riwmzuiv1">
                  {selectedLog.entityName}
                </p>
              </div>}

            {/* Details */}
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800/30" data-id="element-984" data-mp="2ym826ynmm9mr">
              <p className="text-xs font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-2" data-id="element-985" data-mp="2tuoxh5ikmjjy">
                Detalles de la Acción
              </p>
              <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap" data-id="element-986" data-mp="wx11oymira2k">
                {selectedLog.details || 'Sin detalles adicionales.'}
              </p>
            </div>

            {/* Before / After Changes */}
            {selectedLog.action === 'update' && (selectedLog.changes && selectedLog.changes.length > 0 ? <div style={{
          background: 'var(--color-background-primary)',
          border: '0.5px solid var(--color-border-tertiary)',
          borderRadius: 'var(--border-radius-lg)',
          overflow: 'hidden'
        }} data-id="element-987" data-mp="9a2ny8psag8c">
                  {/* Header */}
                  <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 16px',
            borderBottom: '0.5px solid var(--color-border-tertiary)'
          }} data-id="element-988" data-mp="1to32b4bqah2n">
                    <span style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: '#EEEDFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }} data-id="element-989" data-mp="3e3reaye8vtmn">
                      <PencilIcon className="w-4 h-4" style={{
                color: '#534AB7'
              }} data-id="element-990" data-mp="fmx7m5gf0ju6" />
                    </span>
                    <div data-id="element-991" data-mp="2hfld6lgj7jd4">
                      <p style={{
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--color-text-primary)',
                margin: 0
              }} data-id="element-992" data-mp="2cg8hdb7on1ye">
                        Cambios realizados
                      </p>
                      <p style={{
                fontSize: 12,
                color: 'var(--color-text-secondary)',
                margin: 0
              }} data-id="element-993" data-mp="2nw4syj3wlstx">
                        {selectedLog.changes.length}{' '}
                        {selectedLog.changes.length === 1 ? 'campo' : 'campos'}{' '}
                        modificado{selectedLog.changes.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  {/* Fields */}
                  <div style={{
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10
          }} data-id="element-994" data-mp="1cfdgno8z6zfb">
                    {selectedLog.changes.map((change, i) => <div key={i} style={{
              borderRadius: 'var(--border-radius-md)',
              overflow: 'hidden',
              border: '0.5px solid var(--color-border-tertiary)'
            }} data-id="element-995" data-mp="3qopdpvs4rizd" data-mpi={i}>
                        <div style={{
                background: 'var(--color-background-secondary)',
                padding: '6px 12px',
                borderBottom: '0.5px solid var(--color-border-tertiary)'
              }} data-id="element-996" data-mp="370891uydm9p7">
                          <span style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'var(--color-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }} data-id="element-997" data-mp="2szxjqwcpt365">
                            {change.field}
                          </span>
                        </div>
                        <div style={{
                display: 'flex'
              }} data-id="element-998" data-mp="29exaskw2yhi2">
                          <div style={{
                  flex: 1,
                  padding: '10px 14px',
                  background: '#FFF5F5'
                }} data-id="element-999" data-mp="323kre35vwipa">
                            <div style={{
                    fontSize: 11,
                    color: '#A32D2D',
                    fontWeight: 500,
                    marginBottom: 4
                  }} data-id="element-1000" data-mp="3g3c9aeziuj8k">
                              Antes
                            </div>
                            <div style={{
                    fontSize: 13,
                    color: '#791F1F'
                  }} data-id="element-1001" data-mp="1j4dcgg1esg3r">
                              {change.oldValue || <span style={{
                      fontStyle: 'italic',
                      opacity: 0.6
                    }} data-id="element-1002" data-mp="qhsid7tjtpwi">
                                  vacío
                                </span>}
                            </div>
                          </div>
                          <div style={{
                  flex: 1,
                  padding: '10px 14px',
                  background: '#F2FAF0'
                }} data-id="element-1003" data-mp="26v0mp7ysrhth">
                            <div style={{
                    fontSize: 11,
                    color: '#3B6D11',
                    fontWeight: 500,
                    marginBottom: 4
                  }} data-id="element-1004" data-mp="e5q8x40ir71p">
                              Después
                            </div>
                            <div style={{
                    fontSize: 13,
                    color: '#27500A',
                    fontWeight: 500
                  }} data-id="element-1005" data-mp="ww7v0pquj4e3">
                              {change.newValue || <span style={{
                      fontStyle: 'italic',
                      opacity: 0.6
                    }} data-id="element-1006" data-mp="33nqkkb0o1yiq">
                                  vacío
                                </span>}
                            </div>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div> : <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30" data-id="element-1007" data-mp="3a37a82yji9ko">
                  <span className="text-lg flex-shrink-0" data-id="element-1008" data-mp="355xlkpiz61xw">⚠️</span>
                  <div data-id="element-1009" data-mp="303q6vs6rg7rv">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300" data-id="element-1010" data-mp="304i1azomfzgk">
                      Detalle no disponible
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5 leading-relaxed" data-id="element-1011" data-mp="1opbf9i8z62u0">
                      Este registro es anterior al sistema de rastreo de
                      cambios. Los registros nuevos sí mostrarán el antes y
                      después de cada campo modificado.
                    </p>
                  </div>
                </div>)}

            {/* Entity Context - lookup related data */}
            <EntityContext log={selectedLog} data-id="element-1012" data-mp="ruyks5i39z91" />

            {/* Close button */}
            <div className="flex justify-end pt-2" data-id="element-1013" data-mp="15mv7tv4whiyk">
              <Button variant="outline" onClick={() => setSelectedLog(null)} data-id="element-1014" data-mp="10pv1dj452e6x">
                Cerrar
              </Button>
            </div>
          </div>
        </Modal> : null}

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Bitácora" isExporting={isExporting} data-id="element-1015" data-mp="1aokl7hfli8pt" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Bitácora a PDF" isExporting={isPdfExporting} data-id="element-1016" data-mp="1rwv90wf00gqq" />
    </div>;
}